import { useAtomValue } from "jotai/index";
import { useMemo } from "react";
import { graphql, readInlineData } from "react-relay";

import {
  checkArticleIsInArchiveWall,
  checkArticleIsInLiteUpgradeWall,
  checkArticleIsInSignInWall,
  checkArticleIsInVelocityWall,
} from "scmp-app/components/article/article-render/hooks/piano-integration/helpers";
import { appAtom } from "scmp-app/lib/app/atoms";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import {
  useLTSHardPaywallABTest,
  useVelocityWallThresholdABTest,
} from "scmp-app/lib/rosetta/a-b-test/hooks";
import { useRosettaCampaign } from "scmp-app/lib/rosetta/hooks/rosetta-campaign";
import { usePaywallStrategies } from "scmp-app/lib/rosetta/hooks/rosetta-paywall-strategies";
import { useRosettaPianoExecution } from "scmp-app/lib/rosetta/hooks/rosetta-piano-execution";
import type { paywallsGetArticlePaywallTagArticle$key } from "scmp-app/queries/__generated__/paywallsGetArticlePaywallTagArticle.graphql";

export const useGetArticlePaywallTag = (article_: paywallsGetArticlePaywallTagArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment paywallsGetArticlePaywallTagArticle on Article @inline {
        flag
        topics {
          entityId
        }

        ...helpersIsSignInWallArticle
        ...helpersIsArchiveWallArticle
        ...helpersIsLiteWallArticle
        ...helpersIsVelocityWallArticle
        ...hooksVelocityWallThresholdABTest
      }
    `,
    article_,
  );

  const { applyVelocityWallThresholdLogic, shouldApplyVelocityWallThresholdTest } =
    useVelocityWallThresholdABTest(article);

  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { getActiveCampaignSettings } = useRosettaCampaign();
  const { getActivePaywallStrategy } = usePaywallStrategies();
  const { isRosettaPianoExecutionReady } = useRosettaPianoExecution(
    navigatorInfo,
    navigatorInfoLoaded,
  );

  const { ltsHardPaywallABTestTag } = useLTSHardPaywallABTest();

  const appSettings = asyncRosettaState?.result?.appSettings ?? {};
  const { litePaywallTimeFrame = 3, oldArticleTimeframe = 5 } = appSettings;

  const isSignInPaywall = checkArticleIsInSignInWall(article);

  // Note: can refactor and include checkArticleIsInVelocityWall into this memorized value
  const isVelocityPaywall = useMemo(
    () =>
      shouldApplyVelocityWallThresholdTest
        ? applyVelocityWallThresholdLogic()
        : checkArticleIsInVelocityWall(article),
    [applyVelocityWallThresholdLogic, article, shouldApplyVelocityWallThresholdTest],
  );

  // Note: can refactor and include checkArticleIsInArchiveWall into this memorized value
  const isArchivePaywall = useMemo(() => {
    if (!isRosettaPianoExecutionReady && asyncRosettaState?.status !== "success") return;

    const activeCampaignSettings = getActiveCampaignSettings();
    const activeArchivePaywallStrategy = getActivePaywallStrategy({ paywallType: "archive" });

    const archiveArticleTimeFrame =
      activeCampaignSettings?.settings?.paywall?.archive?.articleAgeThresholdDays ??
      activeArchivePaywallStrategy?.settings?.paywall?.archive?.articleAgeThresholdDays ??
      oldArticleTimeframe;

    return checkArticleIsInArchiveWall(article, archiveArticleTimeFrame);
  }, [
    article,
    asyncRosettaState?.status,
    getActiveCampaignSettings,
    getActivePaywallStrategy,
    isRosettaPianoExecutionReady,
    oldArticleTimeframe,
  ]);

  const isScmpLiteUser = asyncRosettaState?.result?.user?.isSCMPLiteSubscriber ?? false;
  const isLiteUpgradePaywall = useMemo(
    () => checkArticleIsInLiteUpgradeWall(article, isScmpLiteUser, litePaywallTimeFrame),
    [article, isScmpLiteUser, litePaywallTimeFrame],
  );

  const pianoArticlePaywallCategoryTag = (() => {
    if (!isRosettaPianoExecutionReady && asyncRosettaState?.status !== "success") return;

    const topicIds = article.topics?.map?.(topic => topic?.entityId ?? "") ?? [];
    const paywallStrategySettings = getActivePaywallStrategy({
      flag: article?.flag ?? undefined,
      topicIds,
    });

    if (isLiteUpgradePaywall) return "category/liteUpgrade";
    if (paywallStrategySettings?.settings?.paywall?.type)
      return `category/${paywallStrategySettings.settings.paywall.type}`;
    if (ltsHardPaywallABTestTag) return ltsHardPaywallABTestTag;
    if (isVelocityPaywall) return "category/velocity";
    if (isArchivePaywall) return "category/archive";
    if (isSignInPaywall) return "category/signInWall";
  })();

  return {
    pianoArticlePaywallCategoryTag,
  };
};
